import { UNIT_ORBIT_HEIGHT } from './constants.js';
import { getSphereMath, planetHexasphere } from './hexasphere.js';
import { planetTile } from './planet-terrain.js';
import { UnitPositionTypes, SunRadius } from './types.js';
export const hexagonWidth = 1000;

function sectorToPoint([q, r]) {
	const edge = hexagonWidth / 2;
	return {
		x: ((edge * 3) / 2) * q,
		y: edge * Math.sqrt(3) * (r + q / 2),
	};
}

// https://wrfranklin.org/Research/Short_Notes/pnpoly.html
export function isPointOnMap(corners, point) {
	//l('>>isPointOnMap', corners, point);
	if (Array.isArray(point)) {
		point = {
			x: point[0],
			y: point[1],
		};
	}
	let c = false;
	const vertx = [],
		verty = [];
	if (Array.isArray(corners)) {
		for (const corner of corners) {
			vertx.push(corner.x);
			verty.push(corner.y);
			if (corner.x === point.x && corner.y === point.y) {
				return true;
			}
		}
	} else if (typeof corners === 'object') {
		vertx.push(corners.topLeft.x);
		verty.push(corners.topLeft.y);
		if (corners.topLeft.x === point.x && corners.topLeft.y === point.y) {
			return true;
		}
		vertx.push(corners.topRight.x);
		verty.push(corners.topRight.y);
		if (corners.topRight.x === point.x && corners.topRight.y === point.y) {
			return true;
		}
		vertx.push(corners.bottomRight.x);
		verty.push(corners.bottomRight.y);
		if (
			corners.bottomRight.x === point.x &&
			corners.bottomRight.y === point.y
		) {
			return true;
		}
		vertx.push(corners.bottomLeft.x);
		verty.push(corners.bottomLeft.y);
		if (corners.bottomLeft.x === point.x && corners.bottomLeft.y === point.y) {
			return true;
		}
	} else {
		throw new Error('Unknown type for corners', corners);
	}

	const nvert = vertx.length;
	const { x, y } = point;
	for (let i = 0, j = nvert - 1; i < nvert; j = i++) {
		if (
			verty[i] > y != verty[j] > y &&
			x <
				((vertx[j] - vertx[i]) * (y - verty[i])) / (verty[j] - verty[i]) +
					vertx[i]
		)
			c = !c;
	}
	return c;
}

/**
 * Check if a point (x, y, z) is inside a sphere given by its center point and radius.
 * @param {Object} point - The point checked for being inside the sphere.
 * @param {number} point.x - x coordinate.
 * @param {number} point.y - y coordinate.
 * @param {number} point.z - z coordinate.
 * @param {Object} sphere - The center of the sphere we are testing for.
 * @param {number} sphere.x - x coordinate.
 * @param {number} sphere.y - y coordinate.
 * @param {number} sphere.z - z coordinate.
 * @param {number} radius - The radius of the sphere we are testing for.
 * @returns {boolean} True if the point is inside the sphere, false otherwise.
 */
export function isPointInSphere({ x, y, z }, { x: sx, y: sy, z: sz }, radius) {
	return (
		Math.pow(x - sx, 2) + Math.pow(y - sy, 2) + Math.pow(z - sz, 2) <=
		Math.pow(radius, 2)
	);
}

export function getPlanetPosition(
	sunX,
	sunY,
	orbitSpeed,
	distanceToSun,
	time = Date.now(),
) {
	const speed = 100000; //200; // higher value, slower speed
	const x =
		Math.cos(time * (1.0 / (orbitSpeed * speed)) + 0) * (distanceToSun * 5) +
		sunX;
	const y =
		Math.sin(time * (1.0 / (orbitSpeed * speed)) + 0) * (distanceToSun * 5) +
		sunY;
	const z = 0;
	return { x, y, z };
}

export function getPlanetRotation(
	rotationSpeed = 24 * 60 * 60 * 1000,
	time = Date.now(),
) {
	if (!rotationSpeed) {
		return 0;
	}
	const rotationAngle = (time / rotationSpeed) * 2 * Math.PI;
	return rotationAngle % (2 * Math.PI); // Return the rotation angle within the range [0, 2π)
}

export function distance(a = { x, y, z }, b = { x, y, z }) {
	if (Array.isArray(a) && Array.isArray(b)) {
		return Math.sqrt(
			Math.pow(b[0] - a[0], 2) +
				Math.pow(b[1] - a[1], 2) +
				Math.pow(b[2] - a[2], 2),
		);
	}
	return Math.sqrt(
		Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2) + Math.pow(b.z - a.z, 2),
	);
}

/*globalThis.planetMathCache = {
	'GAS': {},
	'ANY': {},
};*/
export async function getPlanetMath(type, size) {
	if (
		typeof IS_SERVER !== 'undefined' &&
		IS_SERVER &&
		globalThis.hexaspheres
	) {
		if (type === 'GAS') {
			return globalThis.hexaspheres['GAS'][size];
		} else {
			return globalThis.hexaspheres['ANY'][size];
		}
	}
	return getSphereMath(type, size);
}

export async function getSpaceMapRadius() {
	const test = await (
		await mongoDB.collection('spaceTiles').find(
			{},
			{
				sort: { 'sector.0': -1 },
				limit: 1,
			},
		)
	).next();
	if (!test) {
		return 0;
	}
	return test.sector[0] + 1;
}

export function getSunCollisionRadius(sun) {
	return (SunRadius[sun.type] || 25) + 2;
}

export function getPlanetCollisionRadius(planet) {
	return Math.ceil(planet.size / 5) + 0.5;
}

export function getUnitCollisionRadius(unit) {
	return 0.2;
}

// Movement integrates in fixed quanta anchored to the server-stamped course
// time (d.time). Client and server replay the exact same integer steps, so a
// trajectory is a pure function of the step index — call cadence (rAF vs
// server tick) and frame jitter cannot make the two sims diverge.
export const MOVEMENT_STEP_MS = 100;
const MOVEMENT_MAX_CATCHUP_STEPS = 36000; // 1h — backstop for slept tabs

// distance covered in one fixed step at a given speed (% of base)
export function movementStepAdvance(speed) {
	return (((1000 / 60) * speed) / 100 / 1000) * MOVEMENT_STEP_MS;
}

export async function getUnitPosition(unit, obstacles) {
	const now = Date.now();
	const d = unit.destination;

	const speed = 10;
	if (unit.remaining) unit.remaining.speed = speed;
	if (typeof d.time !== 'number') d.time = now;

	let steps = Math.floor((now - d.time) / MOVEMENT_STEP_MS);
	if (steps <= 0) return false;
	if (steps > MOVEMENT_MAX_CATCHUP_STEPS) {
		d.time = now - MOVEMENT_MAX_CATCHUP_STEPS * MOVEMENT_STEP_MS;
		steps = MOVEMENT_MAX_CATCHUP_STEPS;
	}
	const advance = movementStepAdvance(speed);

	for (let step = 0; step < steps; step++) {
		// consume exactly one quantum; the remainder stays on d.time so no
		// simulation time is ever lost or double-counted
		d.time += MOVEMENT_STEP_MS;

		if (d.type === UnitPositionTypes.ORBIT) {
			// chase the ACTUAL tile orbit point (3D, tracks the orbiting and
			// rotating planet at the deterministic step time) — height is gained
			// gradually during flight, insertion is just the last small step
			const { x, y, z } = await getOrbitPosition(d);
			d.x = x;
			d.y = y;
			d.z = z;
		}

		const direction = {
			x: d.x - unit.position.x,
			y: d.y - unit.position.y,
			z: d.z - unit.position.z,
		};
		const distanceToDestination = Math.sqrt(
			direction.x ** 2 + direction.y ** 2 + direction.z ** 2,
		);

		if (distanceToDestination <= advance) {
			Object.assign(unit.position, d);
			return true; // stop
		}
		direction.x /= distanceToDestination;
		direction.y /= distanceToDestination;
		direction.z /= distanceToDestination;

		unit.position.x += direction.x * advance;
		unit.position.y += direction.y * advance;
		unit.position.z += direction.z * advance;

		if (d.type === UnitPositionTypes.ORBIT) {
			// the destination planet is guarded by its ORBIT SHELL instead of the
			// avoidance zone: a step that dips inside (far-side approaches,
			// tile-to-tile moves) is projected back onto the shell, so the unit
			// slides along the sphere to the tile — corrections stay <= advance,
			// no zone kick-out, and the planet body is impenetrable
			const sunPosition = d.sunPosition || sectorToPoint(d.sector);
			const planetPosition = getPlanetPosition(
				sunPosition.x,
				sunPosition.y,
				d.orbitSpeed,
				d.distanceToSun,
				d.time,
			);
			const shellRadius = Math.ceil(d.planetSize / 5) + UNIT_ORBIT_HEIGHT;
			const px = unit.position.x - planetPosition.x;
			const py = unit.position.y - planetPosition.y;
			const pz = unit.position.z - planetPosition.z;
			const distToCenter = Math.sqrt(px * px + py * py + pz * pz);
			if (distToCenter > 0 && distToCenter < shellRadius) {
				const f = shellRadius / distToCenter;
				unit.position.x = planetPosition.x + px * f;
				unit.position.y = planetPosition.y + py * f;
				unit.position.z = planetPosition.z + pz * f;
			}
		}

		for (const obstacle of obstacles) {
			// the destination planet is handled by the shell guard above — its
			// avoidance zone would fight the shell path (the shell lies inside it)
			if (
				d.type === UnitPositionTypes.ORBIT &&
				obstacle.index !== undefined &&
				obstacle.index === d.planetIndex &&
				obstacle.sector?.[0] === d.sector?.[0] &&
				obstacle.sector?.[1] === d.sector?.[1]
			) {
				continue;
			}
			if (typeof obstacle.$collisions !== 'undefined' || obstacle === unit) {
				continue;
			}
			const combinedRadius = obstacle.r + unit.r;
			if (isNaN(combinedRadius)) {
				console.error('WRONG RADIUS', combinedRadius, unit, obstacle);
				continue;
			}
			const distanceToObstacle = Math.sqrt(
				(unit.position.x - obstacle.position.x) ** 2 +
					(unit.position.y - obstacle.position.y) ** 2 +
					(unit.position.z - obstacle.position.z) ** 2,
			);
			if (distanceToObstacle >= combinedRadius) continue;

			const destinationInsideZone =
				distance(d, obstacle.position) < combinedRadius;
			if (destinationInsideZone) {
				// destination inside a blocking zone: park at the zone boundary
				// instead of fighting the push-out forever (ORBIT targets sit
				// just OUTSIDE their planet's zone, so they never land here)
				const avoidance = calculateAvoidanceVector(
					unit, obstacle, combinedRadius, distanceToObstacle,
					distanceToDestination, 0,
				);
				unit.position.x += avoidance.x;
				unit.position.y += avoidance.y;
				unit.position.z += avoidance.z;
				return 'collision'; // stop where we are
			}

			const avoidanceVector = calculateAvoidanceVector(
				unit,
				obstacle,
				combinedRadius,
				distanceToObstacle,
				distanceToDestination,
				advance,
			);
			unit.position.x += avoidanceVector.x;
			unit.position.y += avoidanceVector.y;
			unit.position.z += avoidanceVector.z;
		}
	}

	return false;
}

export function calculateAvoidanceVector(
	unit,
	obstacle,
	combinedRadius,
	distanceToObstacle,
	distanceToDestination,
	advance,
) {
	const safeDistance = distanceToObstacle || 0.000001;
	const normal = {
		x: unit.position.x - obstacle.position.x,
		y: unit.position.y - obstacle.position.y,
		z: unit.position.z - obstacle.position.z,
	};

	normal.x /= safeDistance;
	normal.y /= safeDistance;
	normal.z /= safeDistance;

	const targetFromObstacle = {
		x: unit.destination.x - obstacle.position.x,
		y: unit.destination.y - obstacle.position.y,
		z: unit.destination.z - obstacle.position.z,
	};
	const clockwise = { x: normal.y, y: -normal.x, z: 0 };
	const counterClockwise = { x: -normal.y, y: normal.x, z: 0 };
	const clockwiseDot = clockwise.x * targetFromObstacle.x + clockwise.y * targetFromObstacle.y;
	const counterClockwiseDot = counterClockwise.x * targetFromObstacle.x + counterClockwise.y * targetFromObstacle.y;
	const tangent = clockwiseDot > counterClockwiseDot ? clockwise : counterClockwise;
	const tangentLength = Math.sqrt(tangent.x ** 2 + tangent.y ** 2) || 1;
	tangent.x /= tangentLength;
	tangent.y /= tangentLength;

	const clearance = combinedRadius + 0.001;
	const correction = {
		x: obstacle.position.x + normal.x * clearance - unit.position.x,
		y: obstacle.position.y + normal.y * clearance - unit.position.y,
		z: obstacle.position.z + normal.z * clearance - unit.position.z,
	};
	const slide = Math.max(0, Math.min(advance || 0, distanceToDestination || advance || 0));
	return {
		x: correction.x + tangent.x * slide,
		y: correction.y + tangent.y * slide,
		z: correction.z,
	};
}

// generator
export function getHexagonalGeometry(width = 10, format) {
	const edge = width / 2;
	const halfEdge = edge / 2;
	const height = edge * Math.sqrt(3);
	const halfHeight = height / 2;
	if (format == 'buffer') {
		return [
			halfEdge + edge,
			height,
			0,
			halfEdge,
			height,
			0,
			0,
			halfHeight,
			0,
			halfEdge,
			0,
			0,
			halfEdge + edge,
			0,
			0,
			width,
			halfHeight,
			0,
			halfEdge + edge,
			height,
			0,
		];
	} else {
		return [
			{ x: halfEdge + edge, y: height, z: 0 },
			{ x: halfEdge, y: height, z: 0 },
			{ x: 0, y: halfHeight, z: 0 },
			{ x: halfEdge, y: 0, z: 0 },
			{ x: halfEdge + edge, y: 0, z: 0 },
			{ x: width, y: halfHeight, z: 0 },
			{ x: halfEdge + edge, y: height, z: 0 },
		];
	}
}

export function tileDataFromPlanetByIndex(planet, index) {
	// terrain/height DERIVE from the seed (patch overrides applied);
	// forests/resources are rolled-at-creation gameplay data from the doc
	const tile = planetTile(planet, index);
	return {
		index,
		terrain: tile.t,
		height: tile.h,
		forest: planet.forests?.[index],
		resources: planet.resources?.[index],
	};
}

export function translateOnAxis(objectPosition, axis, distance) {
	// Calculate the normalized axis vector
	const magnitude = Math.sqrt(
		axis[0] * axis[0] + axis[1] * axis[1] + axis[2] * axis[2],
	);
	const normalizedAxis = [
		axis[0] / magnitude,
		axis[1] / magnitude,
		axis[2] / magnitude,
	];

	// Calculate the translation vector by scaling the normalized axis by the distance
	const translation = [
		normalizedAxis[0] * distance,
		normalizedAxis[1] * distance,
		normalizedAxis[2] * distance,
	];

	const newPosition = [
		objectPosition[0] + translation[0],
		objectPosition[1] + translation[1],
		objectPosition[2] + translation[2],
	];

	// Update the object's position by adding the translation vector
	return newPosition;
}

function quaternionToMatrix(q) {
	const xx = q[0] * q[0];
	const xy = q[0] * q[1];
	const xz = q[0] * q[2];
	const xw = q[0] * q[3];
	const yy = q[1] * q[1];
	const yz = q[1] * q[2];
	const yw = q[1] * q[3];
	const zz = q[2] * q[2];
	const zw = q[2] * q[3];

	return [
		[1 - 2 * (yy + zz), 2 * (xy - zw), 2 * (xz + yw)],
		[2 * (xy + zw), 1 - 2 * (xx + zz), 2 * (yz - xw)],
		[2 * (xz - yw), 2 * (yz + xw), 1 - 2 * (xx + yy)],
	];
}

// Rotate a vector by a rotation matrix
function rotateVector(vector, matrix) {
	return [
		vector[0] * matrix[0][0] +
			vector[1] * matrix[0][1] +
			vector[2] * matrix[0][2],
		vector[0] * matrix[1][0] +
			vector[1] * matrix[1][1] +
			vector[2] * matrix[1][2],
		vector[0] * matrix[2][0] +
			vector[1] * matrix[2][1] +
			vector[2] * matrix[2][2],
	];
}

// Get world position
function getWorldPosition(localPosition, planetPosition, planetQuaternion) {
	const rotationMatrix = quaternionToMatrix(planetQuaternion);
	const rotatedPosition = rotateVector(localPosition, rotationMatrix);
	return [
		planetPosition[0] + rotatedPosition[0],
		planetPosition[1] + rotatedPosition[1],
		planetPosition[2] + rotatedPosition[2],
	];
}

export async function getPlanetTileCenter(planetType, planetSize, tileIndex) {
	return (await getPlanetMath(planetType, planetSize)).centers[tileIndex];
}

/**
 * @experience if unit moves weirdly in another direction check if rotationSpeed is present first
 */
// world-abstract position of a tile's orbit point at time d.time.
// Tile centers are SCENE-local ([0]=x, [1]=up, [2]=z); the abstract map plane
// is (x, y) = (scene x, scene z) with height on abstract z. The renderer spins
// the planet via mesh.rotation.y = angle, which on the (x, scene z) plane is
// x' = x cos + y sin, y' = -x sin + y cos — the sim must rotate the SAME way
// or flight targets end up mirrored to the visible tile.
export async function getOrbitPosition(d) {
	// getPlanetPosition expects the SUN's world point, not hex indices
	const sunPosition = d.sunPosition || sectorToPoint(d.sector);
	const planetPosition = getPlanetPosition(
		sunPosition.x,
		sunPosition.y,
		d.orbitSpeed,
		d.distanceToSun,
		d.time,
	);
	const center = await getPlanetTileCenter(
		d.planetType,
		d.planetSize,
		d.tileIndex,
	);
	const rotationAngle = getPlanetRotation(d.rotationSpeed, d.time);
	const cos = Math.cos(rotationAngle);
	const sin = Math.sin(rotationAngle);
	const rotatedCenter = [
		center[0] * cos + center[2] * sin,
		-center[0] * sin + center[2] * cos,
		center[1],
	];
	const orbit = translateOnAxis(
		rotatedCenter,
		rotatedCenter,
		UNIT_ORBIT_HEIGHT,
	);

	return {
		x: planetPosition.x + orbit[0],
		y: planetPosition.y + orbit[1],
		z: planetPosition.z + orbit[2],
	};
}

